import React, { useMemo } from "react";

import { Icon } from "@cobasi/ds-icons";
import ChevronLeft from "@cobasi/ds-icons/solid/chevronLeft";
import ChevronRight from "@cobasi/ds-icons/solid/chevronRight";

import type { PaginationProps } from "./pagination.types";

import s from "./pagination.module.scss";

const PAGE_SIZE = 12;

const Pagination = ({
  pageCount,
  onPageChange,
  forcePage,
  current,
  showLessItems,
  colors
}: PaginationProps) => {
  const { paginationColor, iconColor } = colors || {};

  const currentPage = current || forcePage || 1;
  const maxVisiblePages = showLessItems ? 5 : 7;

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];

    if (pageCount <= maxVisiblePages) {
      for (let i = 1; i <= pageCount; i++) {
        pages.push(i);
      }
    } else {
      const leftSiblingIndex = Math.max(currentPage - 1, 1);
      const rightSiblingIndex = Math.min(currentPage + 1, pageCount);

      const shouldShowLeftDots = leftSiblingIndex > 2;
      const shouldShowRightDots = rightSiblingIndex < pageCount - 1;

      if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = showLessItems ? 3 : 5;
        for (let i = 1; i <= leftItemCount; i++) {
          pages.push(i);
        }
        pages.push("right-dots");
        pages.push(pageCount);
      } else if (shouldShowLeftDots && !shouldShowRightDots) {
        pages.push(1);
        pages.push("left-dots");
        const rightItemCount = showLessItems ? 3 : 5;
        for (let i = pageCount - rightItemCount + 1; i <= pageCount; i++) {
          pages.push(i);
        }
      } else if (shouldShowLeftDots && shouldShowRightDots) {
        pages.push(1);
        pages.push("left-dots");
        for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
          pages.push(i);
        }
        pages.push("right-dots");
        pages.push(pageCount);
      }
    }

    return pages;
  }, [currentPage, pageCount, maxVisiblePages, showLessItems]);

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pageCount) {
      onPageChange(page, PAGE_SIZE);
    }
  };

  const handlePrevClick = () => {
    if (currentPage > 1) {
      handlePageClick(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < pageCount) {
      handlePageClick(currentPage + 1);
    }
  };

  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= pageCount;

  return (
    <div
      className={s.pagination}
      style={
        {
          "--pagination-color": paginationColor,
          "--icon-color": iconColor
        } as React.CSSProperties
      }
    >
      <ul>
        <li
          className={`${s["pagination-prev"]} ${isPrevDisabled ? s.disabled : ""}`}
        >
          <button
            type="button"
            data-testid="Previous page"
            title="Anterior"
            onClick={handlePrevClick}
            disabled={isPrevDisabled}
          >
            <Icon icon={ChevronLeft} fontSize={15} />
          </button>
        </li>
        {pageNumbers.map((page, index) => {
          if (typeof page === "string") {
            // Renderiza "..."
            return (
              <li
                key={`dots-${page}-${index}`}
                className={s["pagination-jump"]}
              >
                <button type="button" disabled>
                  ...
                </button>
              </li>
            );
          }
          const isActive = page === currentPage;
          return (
            <li
              key={`page-${page}`}
              className={`${s.paginationItem} ${isActive ? s.active : ""}`}
            >
              <button
                type="button"
                onClick={() => handlePageClick(page)}
                aria-label={`Página ${page}`}
                aria-current={isActive ? "page" : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}
        <li
          className={`${s["pagination-next"]} ${isNextDisabled ? s.disabled : ""}`}
        >
          <button
            type="button"
            data-testid="Next page"
            title="Próximo"
            onClick={handleNextClick}
            disabled={isNextDisabled}
          >
            <Icon icon={ChevronRight} fontSize={15} />
          </button>
        </li>
      </ul>
    </div>
  );
};

export default Pagination;
